/* useSwipeNav — horizontal swipe-to-navigate between mobile tabs.
 *
 * Why this exists: now that pages are locked in place (no accidental
 * sideways drift), an INTENTIONAL horizontal swipe becomes a meaningful
 * gesture. Photographers using the installed iOS Lumetier app can
 * swipe left/right to walk through the bottom-tab destinations without
 * lifting their thumb — exactly how native iOS apps with tab bars feel
 * (Instagram, Twitter, etc.).
 *
 * Safety guards baked in:
 *   • Only triggers on screens < 1024px (lg breakpoint) — desktop has the
 *     sidebar nav and doesn't need swipes.
 *   • Touch threshold: 60px minimum horizontal distance + <40px vertical
 *     wobble — prevents accidental triggers when scrolling vertically.
 *   • Ignores touches that start on inputs, textareas, contenteditables,
 *     or any element that owns its own horizontal scroll (tab strips,
 *     code blocks, image carousels). Without this guard, a user trying
 *     to scroll inside a horizontally-scrollable tab strip would
 *     accidentally jump pages.
 *   • Velocity guard: swipe must complete within 600ms — slow pans
 *     usually mean the user is doing something else.
 *
 * Usage:
 *   useSwipeNav({
 *     onSwipeLeft: () => navigate("/admin/bookings"),
 *     onSwipeRight: () => navigate("/admin"),
 *   });
 */
import { useEffect, useRef } from "react";

const MIN_DISTANCE = 60;
const MAX_VERTICAL = 40;
const MAX_DURATION_MS = 600;
const LG_BREAKPOINT = 1024;

function isInScrollableX(el) {
  // Walk up the DOM checking computed overflow-x. If any ancestor between
  // the touch target and the document root scrolls horizontally on its
  // own, we let it handle the gesture instead of stealing it for tab nav.
  let node = el;
  while (node && node !== document.body) {
    try {
      const style = window.getComputedStyle(node);
      const ox = style.overflowX;
      if ((ox === "auto" || ox === "scroll") && node.scrollWidth > node.clientWidth + 1) {
        return true;
      }
    } catch {
      // SVG nodes etc. don't always have a getComputedStyle path; ignore.
    }
    node = node.parentElement;
  }
  return false;
}

function isInteractiveTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  // The Lightbox + image gallery have their own swipe handlers; bail.
  if (el.closest("[data-swipe-owner]")) return true;
  return false;
}

export default function useSwipeNav({ onSwipeLeft, onSwipeRight, enabled = true } = {}) {
  const stateRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    function onTouchStart(e) {
      if (window.innerWidth >= LG_BREAKPOINT) return;
      const t = e.touches[0];
      if (!t) return;
      if (isInteractiveTarget(e.target) || isInScrollableX(e.target)) return;
      stateRef.current = {
        x: t.clientX,
        y: t.clientY,
        t: Date.now(),
      };
    }

    function onTouchEnd(e) {
      const s = stateRef.current;
      stateRef.current = null;
      if (!s) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      const dt = Date.now() - s.t;
      if (dt > MAX_DURATION_MS) return;
      if (Math.abs(dy) > MAX_VERTICAL) return;
      if (Math.abs(dx) < MIN_DISTANCE) return;
      if (dx < 0 && onSwipeLeft) onSwipeLeft();
      else if (dx > 0 && onSwipeRight) onSwipeRight();
    }

    // `passive: true` because we never preventDefault — we only observe.
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, onSwipeLeft, onSwipeRight]);
}
